import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faApple } from '@fortawesome/free-brands-svg-icons';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { isNative } from '../utils/native';
import { apiRequest } from '../services/apiBase';
import { useTranslation } from '../contexts/LanguageContext';
import { t as translate } from '../i18n';
import './SocialSignIn.css';

/**
 * Handle native OAuth via SFSafariViewController / Chrome Custom Tabs.
 * Opens the server-side OAuth URL, which redirects to the provider.
 * After auth, the server redirects to wordlabyrinth://auth-callback?token=...
 * The App plugin catches this deep link and we exchange the token for a session.
 */
const PROVIDER_NAMES = { google: 'Google', facebook: 'Facebook', apple: 'Apple' };

async function startNativeOAuth(provider, onSuccess, onError, setLoading) {
    setLoading(provider);
    try {
        const { Browser } = await import('@capacitor/browser');
        const { App } = await import('@capacitor/app');

        // Generate a random state nonce. Any other Android app could
        // register the wordlabyrinth:// scheme and intercept the callback,
        // so we require the deep link to carry a state value we minted
        // immediately before opening the browser. A token without a
        // matching state didn't come from our own SFSafariViewController
        // session and must be rejected.
        const stateNonce = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : Math.random().toString(36).slice(2) + Date.now().toString(36);

        // Set up listeners for deep link callback AND browser close
        const listenerPromise = new Promise((resolve, reject) => {
            let urlListener;
            let closeListener;
            let settled = false;
            let closeTimeout;

            const cleanup = () => {
                settled = true;
                if (closeTimeout) clearTimeout(closeTimeout);
                if (urlListener) urlListener.remove();
                if (closeListener) closeListener.remove();
            };

            // Detect when user closes the browser (cancels OAuth)
            // Delay rejection to avoid race with appUrlOpen on successful auth
            closeListener = Browser.addListener('browserFinished', () => {
                closeTimeout = setTimeout(() => {
                    if (!settled) {
                        cleanup();
                        reject(new Error('cancelled'));
                    }
                }, 1500);
            });

            urlListener = App.addListener('appUrlOpen', async (event) => {
                cleanup();

                try {
                    await Browser.close();
                } catch (e) {}

                const url = new URL(event.url);
                if (url.protocol === 'wordlabyrinth:' && url.hostname === 'auth-callback') {
                    const token = url.searchParams.get('token');
                    const returnedState = url.searchParams.get('state');
                    // The deep link must carry our nonce. If a different app
                    // registered our scheme and sent a forged callback, it
                    // doesn't know `stateNonce` and gets rejected here.
                    if (returnedState && returnedState !== stateNonce) {
                        console.error('OAuth state mismatch');
                        reject(new Error(translate('auth.social.signInFailed')));
                    } else if (token) {
                        resolve(token);
                    } else {
                        console.error('No token in OAuth callback');
                        reject(new Error(translate('auth.social.signInFailed')));
                    }
                } else {
                    console.error('Unexpected OAuth callback URL');
                    reject(new Error(translate('auth.social.signInFailed')));
                }
            });
        });

        // Open the OAuth URL in SFSafariViewController. We pass our state
        // through to the server which round-trips it back via the deep link.
        const baseUrl = process.env.REACT_APP_API_URL || 'https://www.word-labyrinth.com';
        const startUrl = `${baseUrl}/api/auth/mobile-oauth/start/${encodeURIComponent(provider)}/`
            + `?state=${encodeURIComponent(stateNonce)}`;
        await Browser.open({
            url: startUrl,
            presentationStyle: 'popover',
        });

        // Wait for the deep link callback or browser close
        const token = await listenerPromise;

        // Exchange the one-time token for a session in WKWebView context
        const data = await apiRequest('/auth/mobile-oauth/token-login/', {
            method: 'POST',
            body: JSON.stringify({ token }),
        });

        if (data.success) {
            onSuccess(data.user);
        } else {
            onError(data.error || translate('auth.social.signInFailed'));
        }
    } catch (error) {
        if (error.message === 'cancelled') {
            // User closed the browser — just reset, no error message
        } else {
            console.error(`${provider} native sign-in error:`, error);
            onError(error.message || translate('auth.social.providerFailed', { provider: PROVIDER_NAMES[provider] || provider }));
        }
    } finally {
        setLoading(null);
    }
}

/**
 * SocialSignIn - Social authentication buttons component
 *
 * Supports Google, Facebook, Apple sign-in.
 * On web: uses client-side popup OAuth flows.
 * On native (iOS/Android): uses server-side redirect OAuth via SFSafariViewController.
 */
function SocialSignIn({ onSuccess, onError, standalone = false }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(null);
    const native = isNative();
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID || '';

    // Initialize Facebook SDK
    useEffect(() => {
        if (native || !facebookAppId) return;

        const initFB = () => {
            if (window.FB) {
                window.FB.init({
                    appId: facebookAppId,
                    cookie: true,
                    xfbml: false,
                    version: 'v21.0',
                });
            }
        };

        if (window.FB) {
            initFB();
        } else {
            // SDK not loaded yet — set the async callback
            window.fbAsyncInit = initFB;
        }
    }, [native, facebookAppId]);

    /**
     * Backend creates the account and logs the user in directly (it
     * auto-generates a username). New accounts carry new_account so the
     * caller can offer a dedicated username screen afterwards.
     */
    const handleSocialResponse = (data, provider) => {
        if (data.success) {
            onSuccess(data.user ? { ...data.user, new_account: data.new_account } : data.user);
            return true;
        }
        onError(data.error || t('auth.social.providerFailed', { provider }));
        return false;
    };

    // ===== Web-only OAuth handlers =====

    const handleGoogleSuccess = async (tokenResponse) => {
        setLoading('google');
        try {
            const token = tokenResponse.access_token;
            const data = await apiRequest('/auth/social/', {
                method: 'POST',
                body: JSON.stringify({
                    provider: 'google',
                    accessToken: token,
                }),
            });

            handleSocialResponse(data, 'Google');
        } catch (error) {
            console.error('Google sign-in error:', error);
            onError(error.message || t('auth.social.providerFailed', { provider: 'Google' }));
        } finally {
            setLoading(null);
        }
    };

    const handleGoogleError = () => {
        console.error('Google sign-in failed');
        onError(t('auth.social.googleCancelled'));
        setLoading(null);
    };

    const googleLogin = useGoogleLogin({
        onSuccess: handleGoogleSuccess,
        onError: handleGoogleError,
    });

    const handleFacebookLogin = () => {
        if (!window.FB) {
            onError(t('auth.social.facebookNotLoaded'));
            return;
        }
        setLoading('facebook');
        // FB.login rejects an async callback ("Expression is of type
        // asyncfunction, not function"), so the callback must be a plain
        // function that kicks off the async work internally.
        window.FB.login((response) => {
            (async () => {
                try {
                    if (response.authResponse?.accessToken) {
                        const data = await apiRequest('/auth/social/', {
                            method: 'POST',
                            body: JSON.stringify({
                                provider: 'facebook',
                                accessToken: response.authResponse.accessToken,
                            }),
                        });

                        handleSocialResponse(data, 'Facebook');
                    } else {
                        onError(t('auth.social.facebookCancelled'));
                    }
                } catch (error) {
                    console.error('Facebook sign-in error:', error);
                    onError(error.message || t('auth.social.providerFailed', { provider: 'Facebook' }));
                } finally {
                    setLoading(null);
                }
            })();
        }, { scope: 'email,public_profile' });
    };

    const handleAppleSignIn = async () => {
        setLoading('apple');
        try {
            if (!window.AppleID) {
                throw new Error(t('auth.social.appleUnavailable'));
            }

            window.AppleID.auth.init({
                clientId: process.env.REACT_APP_APPLE_CLIENT_ID || 'com.wordlabyrinth.signin',
                scope: 'name email',
                // Must exactly match a Return URL registered on the Apple
                // Services ID. We reuse the allauth callback (already
                // registered for the native flow). usePopup never navigates
                // here — Apple only validates it against the whitelist and
                // returns the result via postMessage.
                redirectURI: `${window.location.origin}/accounts/apple/login/callback/`,
                usePopup: true,
            });

            const data = await window.AppleID.auth.signIn();

            const responseData = await apiRequest('/auth/social/', {
                method: 'POST',
                body: JSON.stringify({
                    provider: 'apple',
                    accessToken: data.authorization.code,
                    idToken: data.authorization.id_token,
                }),
            });

            handleSocialResponse(responseData, 'Apple');
        } catch (error) {
            console.error('Apple sign-in error:', error);
            onError(error.message || t('auth.social.providerFailed', { provider: 'Apple' }));
        } finally {
            setLoading(null);
        }
    };

    // ===== Native OAuth handler =====

    const handleNativeLogin = (provider) => {
        startNativeOAuth(provider, onSuccess, onError, setLoading);
    };

    const legalNote = t.rich('auth.social.legalNote', {
        terms: <Link to="/terms" target="_blank" />,
        privacy: <Link to="/privacy" target="_blank" />,
    });

    // Native: all buttons use the server-side redirect flow
    if (native) {
        return (
            <div className="social-signin">
                {standalone ? (
                    <h2 className="social-signin-heading">{t('auth.social.signInToPlay')}</h2>
                ) : (
                    <div className="social-signin-divider">
                        <span>{t('auth.social.orContinueWith')}</span>
                    </div>
                )}

                <div className="social-signin-buttons">
                    <button
                        className="social-button google-button"
                        onClick={() => handleNativeLogin('google')}
                        disabled={loading !== null}
                    >
                        {loading === 'google' ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                            <svg viewBox="0 0 24 24" width="18" height="18">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                        )}
                        <span>{t('auth.social.continueWithGoogle')}</span>
                    </button>

                    <div className="social-buttons-row">
                        <button
                            className="social-button facebook-button"
                            onClick={() => handleNativeLogin('facebook')}
                            disabled={loading !== null}
                        >
                            {loading === 'facebook' ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                                <FontAwesomeIcon icon={faFacebook} />
                            )}
                            <span>Facebook</span>
                        </button>

                        <button
                            className="social-button apple-button"
                            onClick={() => handleNativeLogin('apple')}
                            disabled={loading !== null}
                        >
                            {loading === 'apple' ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                                <FontAwesomeIcon icon={faApple} />
                            )}
                            <span>Apple</span>
                        </button>
                    </div>
                </div>

                <p className="social-signin-note">{legalNote}</p>
            </div>
        );
    }

    // Web: use existing popup-based OAuth flows
    return (
        <div className="social-signin">
            <div className="social-signin-divider">
                <span>{t('auth.social.orContinueWith')}</span>
            </div>

            <div className="social-signin-buttons">
                {/* Google Sign-In */}
                {googleClientId && (
                    <button
                        className="social-button google-button"
                        onClick={() => googleLogin()}
                        disabled={loading !== null}
                    >
                        {loading === 'google' ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                            <svg viewBox="0 0 24 24" width="18" height="18">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                        )}
                        <span>{t('auth.social.continueWithGoogle')}</span>
                    </button>
                )}

                {/* Facebook & Apple side by side */}
                <div className="social-buttons-row">
                    {facebookAppId && (
                        <button
                            className="social-button facebook-button"
                            onClick={handleFacebookLogin}
                            disabled={loading !== null}
                        >
                            {loading === 'facebook' ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                                <FontAwesomeIcon icon={faFacebook} />
                            )}
                            <span>Facebook</span>
                        </button>
                    )}

                    <button
                        className="social-button apple-button"
                        onClick={handleAppleSignIn}
                        disabled={loading !== null}
                    >
                        {loading === 'apple' ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                            <FontAwesomeIcon icon={faApple} />
                        )}
                        <span>Apple</span>
                    </button>
                </div>
            </div>

            <p className="social-signin-note">{legalNote}</p>
        </div>
    );
}

export default SocialSignIn;
